
import React, { useEffect, useState, useContext, useMemo , useRef} from 'react';
import { styled } from '@mui/material/styles';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import useCommonFunctionHooks from '../../../../Hooks/useCommonFunctionHooks';
import AlertContext from '../../../../../src/context/AlertContext';
import { useParams } from 'react-router-dom';
import constants from '../../../../constants';


const processData = (raw) => {
  const attendance = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  const scores     = ['0-20', '20-40', '40-60', '60-80', '80-100'];

  const grid = scores.map((_, sIdx) =>
    attendance.map((_, aIdx) => ({
      value      : 0,
      attendance : attendance[aIdx],
      scoreRange : scores[sIdx],
    }))
  );

  raw.forEach(([aIdx, sIdx, val]) => {
    if (grid[sIdx] && grid[sIdx][aIdx]) grid[sIdx][aIdx].value = val;
  });
  return grid;
};

const getColour = (v, max) => {
  if (v === 0 || max === 0) return '#EFEFEF';
  const step = max / 3;
  if (v <= step)      return '#FEE0D9';
  if (v <= step * 2)  return '#FB9A85';
  return '#F96343';
};



//    styled cells / tooltip
const HeatmapCell = styled('div', {
  shouldForwardProp: (prop) => prop !== 'bg' && prop !== 'clickable',
})(({ bg, clickable }) => ({
  width: 26,
  height: 26,
  backgroundColor: bg,
  borderRadius: 4,
//   border: '2px solid #fff',
  cursor: clickable ? 'pointer' : 'default',
}));

const TooltipBox = styled('div')({
  position: 'absolute',
  zIndex: 1000,
  width: 160,
  borderRadius: 8,
  padding: 12,
  boxShadow: '0 3px 6px rgba(0,0,0,.15)',
  background: '#fff',
  pointerEvents: 'none',
});



// legend

function Legend() {
  const colors = ['#EFEFEF', '#FEE0D9', '#FB9A85', '#F96343'];
  return (
    <div className="legend">
      <div className="legend-header">
        <span className='legend-name'>Less</span>
        <span className='legend-name'>More</span>
      </div>
      <div className="legend-scale">
        {colors.map((c, i) => (
          <div key={i} style={{ background: c }} />
        ))}
      </div>
    </div>
  );
}


//    main component

export default function AttendanceScoreHeatmap() {
  const [scoreRatioData, setScoreRatioData] = useState([]);
  const data          = processData(scoreRatioData);
  const maxIntensity  = useMemo(() => {
    const vals = scoreRatioData.map((d) => d[2]);
    return vals.length ? Math.max(...vals) : 0;
  }, [scoreRatioData]);

  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, data: null });
  const Alert                 = useContext(AlertContext);
  const axios                 = useAxiosPrivate();
  const { getCatchBlockDetails } = useCommonFunctionHooks();
  const { id }                = useParams();

  useEffect(() => {
    Alert.setIsLoaded(true);
    axios
      .get('/node/admin/batch/db/s_ratio', {
        params  : { batchid: id },
        headers : { 'Content-type': 'application/json' },
      })
      .then((res) =>
        res.data.resultCode === constants.RESULT_STATUS.TECHNICAL_ERROR
          ? Alert.setShowNotify({ show: true, title: 'Info', msg: res.data.msg })
          : setScoreRatioData(res.data.data.scoreRatioRes)
      )
      .catch(getCatchBlockDetails)
      .finally(() => Alert.setIsLoaded(false));
  }, []); 

  const showTip = (e, cell) => {
    if (cell.value > 0) {
      const r = e.currentTarget.getBoundingClientRect();
      setTooltip({ show: true, x: r.right + 10, y: r.top, data: cell });
    }
  };
  const hideTip = () => setTooltip({ show: false, x: 0, y: 0, data: null });

  const attendanceVals = [10,20,30,40,50,60,70,80,90,100];
  const scoreRanges    = ['10-20','20-40','40-60','60-80','80-100'];

  const cellRefs = useRef(
  Array.from({ length: 5 }, () => Array.from({ length: 10 }, () => React.createRef()))
);

  return (
    <div className="heatmap-wrapper">
      <div className="heatmap-header">
        <span>Attendance&nbsp;-&nbsp;Score Ratio</span>
        <Legend />
      </div>

      <p className="axis-title">Score</p>

      <div className="heatmap-body">
        <div className="y-axis">
          {scoreRanges.slice().reverse().map((r) => (
            <div key={r} className="y-label">{r}</div>
          ))}
        </div>

        <div className="grid-area">
          {data.slice().reverse().map((row, rIdx) => (
            <div key={rIdx} className="grid-row">
              {row.map((cell, cIdx) => (
                <HeatmapCell
                  key={cIdx}
                  bg={getColour(cell.value, maxIntensity)}
                  clickable={cell.value > 0}
                  onMouseEnter={(e) => showTip(e, cell)}
                  onMouseLeave={hideTip}
                />
              ))}
            </div>
          ))}

          {/* {data.slice().reverse().map((row, rIdx) => (
  <div key={rIdx} className="grid-row">
    {row.map((cell, cIdx) => {
      const actualRow = data.length - 1 - rIdx;
      return (
        <HeatmapCell
          key={cIdx}
          ref={cellRefs.current[actualRow][cIdx]}
          bg={getColour(cell.value, maxIntensity)}
          clickable={cell.value > 0}
          // onMouseEnter={() => showTip(actualRow, cIdx, cell)}
          onMouseEnter={() => showTip(cellRefs.current[actualRow][cIdx], cell)}
          onMouseLeave={hideTip}
        />
      );
    })}
  </div>
))} */}

          <div className="x-axis">
            {attendanceVals.map((v) => (
              <div key={v} className="x-label">{v}</div>
            ))}
          </div>
          <div className="attendance-title">Attendance</div >
        </div>
      </div>

      {tooltip.show && tooltip.data && (
        <TooltipBox style={{ position:"fixed", left: tooltip.x, top: tooltip.y }} className="heatmap-tooltip-box">
          <p className="tooltip-count">{tooltip.data.value} Students</p>
          <p className="tooltip-line">Attendance : {tooltip.data.attendance}%</p>
          <p className="tooltip-line">Score : {tooltip.data.scoreRange}% avg</p>
        </TooltipBox>
      )}

    </div>
  );
}




